import { useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { storageService } from '../services/storageService';

type PersistedStateOptions<T> = {
  serialize?: (value: T) => string;
  deserialize?: (raw: string) => T;
  enabled?: boolean;
  onError?: (error: unknown) => void;
};

const readStorageValue = <T,>(
  key: string,
  defaultValue: T,
  options?: PersistedStateOptions<T>
): T => {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const raw = storageService.getItem(key);
    if (raw === null) return defaultValue;
    if (options?.deserialize) {
      return options.deserialize(raw);
    }
    if (typeof defaultValue === 'string') {
      return raw as unknown as T;
    }
    return JSON.parse(raw) as T;
  } catch (error) {
    options?.onError?.(error);
    return defaultValue;
  }
};

const writeStorageValue = <T,>(
  key: string,
  value: T,
  options?: PersistedStateOptions<T>
) => {
  if (typeof window === 'undefined') return;
  try {
    if (options?.serialize) {
      storageService.setItem(key, options.serialize(value));
      return;
    }
    if (typeof value === 'string') {
      storageService.setItem(key, value);
      return;
    }
    storageService.setItem(key, JSON.stringify(value));
  } catch (error) {
    options?.onError?.(error);
  }
};

export const usePersistedState = <T,>(
  key: string | null,
  defaultValue: T,
  options?: PersistedStateOptions<T>
): [T, Dispatch<SetStateAction<T>>] => {
  const isEnabled = options?.enabled ?? true;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [state, setState] = useState<T>(() => {
    if (!key || !isEnabled) return defaultValue;
    return readStorageValue(key, defaultValue, optionsRef.current);
  });

  useEffect(() => {
    if (!key || !isEnabled) return;
    setState(readStorageValue(key, defaultValue, optionsRef.current));
  }, [key, isEnabled]);

  useEffect(() => {
    if (!key || !isEnabled) return;
    writeStorageValue(key, state, optionsRef.current);
  }, [key, isEnabled, state]);

  return [state, setState];
};

export default usePersistedState;
